// eslint-disable-next-line import/no-cycle
import { adminApi } from '../includes/contentManagementApi';
// eslint-disable-next-line import/no-cycle
import notificationTemplateService from './notificationTemplateService';

export default {
  /**
   * Get all assessment schemas
   */
  getAllAssessmentSchemas() {
    return adminApi.get('/schemas');
  },

  /**
   * Get a specific assessment schema by ID
   * @param {string} id - ID of the assessment
   */
  getAssessmentSchema(id) {
    return adminApi.get(`/schemas/${id}`);
  },

  /**
   * Create a new assessment schema
   * @param {Object} assessmentData - The assessment data
   */
  createAssessmentSchema(assessmentData) {
    return adminApi.post('/schemas', assessmentData);
  },

  /**
   * Update an assessment schema
   * @param {string} id - ID of the assessment
   * @param {Object} assessmentData - The updated assessment data
   */
  updateAssessmentSchema(id, assessmentData) {
    return adminApi.put(`/schemas/${id}`, assessmentData);
  },

  /**
   * Update reflection guideline configuration for a schema
   * @param {string} id - ID of the assessment schema
   * @param {Object} config - The reflection guideline configuration
   */
  updateReflexionGuidelineConfig(id, config) {
    return adminApi.put(`/schemas/${id}/reflexion-guideline-config`, config);
  },

  /**
   * Delete an assessment schema
   * @param {string} id - ID of the assessment
   */
  deleteAssessmentSchema(id) {
    return adminApi.delete(`/schemas/${id}`);
  },

  // Questions endpoints

  /**
   * Get all questions for an assessment
   * @param {string} assessmentId - ID of the assessment
   */
  getQuestions(assessmentId) {
    return adminApi.get(`/schemas/${assessmentId}/questions`);
  },

  /**
   * Get a specific question
   * @param {string} assessmentId - ID of the assessment
   * @param {string} questionId - ID of the question
   */
  getQuestion(assessmentId, questionId) {
    return adminApi.get(`/schemas/${assessmentId}/questions/${questionId}`);
  },

  /**
   * Add a question to an assessment
   * @param {string} assessmentId - ID of the assessment
   * @param {Object} questionData - The question data
   */
  addQuestion(assessmentId, questionData) {
    return adminApi.post(`/schemas/${assessmentId}/questions`, questionData);
  },

  /**
   * Add multiple questions to an assessment
   * @param {string} assessmentId - ID of the assessment
   * @param {Array} questionsData - Array of question data
   */
  addQuestions(assessmentId, questionsData) {
    return adminApi.post(`/schemas/${assessmentId}/questions/bulk`, { questions: questionsData });
  },

  /**
   * Build request body matching backend UpdateQuestionDto (forbidNonWhitelisted).
   * Strips any extra fields at every nesting level.
   */
  _cleanProperties(props) {
    if (!props) return undefined;
    const clean = {};
    if (props.dimensionName !== undefined) clean.dimensionName = props.dimensionName;
    if (props.dimension !== undefined) clean.dimension = props.dimension;
    if (props.content) {
      clean.content = {};
      if (props.content.text_question) clean.content.text_question = props.content.text_question;
      if (props.content.text_questionProgress) {
        clean.content.text_questionProgress = props.content.text_questionProgress;
      }
      if (props.content.progressbar !== undefined) {
        clean.content.progressbar = props.content.progressbar;
      }
    }
    if (props.options) {
      clean.options = {};
      if (props.options.forceInput !== undefined) {
        clean.options.forceInput = props.options.forceInput;
      }
    }
    if (props.data !== undefined) clean.data = props.data;
    return clean;
  },

  _questionUpdatePayload(questionData) {
    const payload = {};
    if (questionData.class !== undefined) payload.class = questionData.class;
    if (questionData.type !== undefined) payload.type = questionData.type;
    if (questionData.properties) {
      payload.properties = this._cleanProperties(questionData.properties);
    }
    if (questionData.viewTimeLogs !== undefined) payload.viewTimeLogs = questionData.viewTimeLogs;
    if (questionData.sticky !== undefined) payload.sticky = questionData.sticky;
    return payload;
  },

  /**
   * Update a question
   * @param {string} assessmentId - ID of the assessment
   * @param {string} questionId - ID of the question
   * @param {Object} questionData - The updated question data
   */
  updateQuestion(assessmentId, questionId, questionData) {
    const body = this._questionUpdatePayload(questionData);
    return adminApi.put(`/schemas/${assessmentId}/questions/${questionId}`, body);
  },

  /**
   * Update multiple questions
   * @param {string} assessmentId - ID of the assessment
   * @param {Array} questionsData - Array of { id, data } objects
   */
  updateQuestions(assessmentId, questionsData) {
    const questions = questionsData.map(({ id, data }) => ({
      id,
      data: this._questionUpdatePayload(data),
    }));
    return adminApi.put(`/schemas/${assessmentId}/questions/bulk`, { questions });
  },

  /**
   * Delete a question
   * @param {string} assessmentId - ID of the assessment
   * @param {string} questionId - ID of the question
   */
  deleteQuestion(assessmentId, questionId) {
    return adminApi.delete(`/schemas/${assessmentId}/questions/${questionId}`);
  },

  /**
   * Delete multiple questions
   * @param {string} assessmentId - ID of the assessment
   * @param {Array} questionIds - Array of question IDs
   */
  deleteQuestions(assessmentId, questionIds) {
    return adminApi.delete(`/schemas/${assessmentId}/questions/bulk`, {
      data: { questionIds },
    });
  },

  // Dimensions endpoints

  /**
   * Update a dimension in the assessment schema
   * @param {string} schemaId - ID of the schema
   * @param {Object} dimension - The dimension data
   */
  updateSchemaDimension(schemaId, dimension) {
    return adminApi.put(`/schemas/${schemaId}/dimensions`, dimension);
  },

  /**
   * Remove a dimension from the assessment schema
   * @param {string} schemaId - ID of the schema
   * @param {string} dimensionValue - The value of the dimension to remove
   */
  removeSchemaDimension(schemaId, dimensionValue) {
    return adminApi.delete(`/schemas/${schemaId}/dimensions/${dimensionValue}`);
  },

  /**
   * Update all dimensions in the assessment schema
   * @param {string} schemaId - ID of the schema
   * @param {Array} dimensions - Array of dimension objects
   */
  updateSchemaDimensions(schemaId, dimensions) {
    return adminApi.put(`/schemas/${schemaId}/dimensions/bulk`, dimensions);
  },

  /**
   * Send DevTips immediately for a specific schema
   * @param {string} schemaId - ID of the schema
   */
  sendDevTipsForSchema(schemaId) {
    return adminApi.post(`/schemas/${schemaId}/send-dev-tips`);
  },

  /**
   * Trigger reflection guidelines immediately for a specific schema
   * @param {string} schemaId - ID of the schema
   */
  triggerReflectionGuidelinesForSchema(schemaId) {
    return adminApi.post(`/schemas/${schemaId}/reflection-guidelines/trigger`);
  },

  /**
   * Create assessment from schema (handles both normal and rotated schemas)
   * @param {string} schemaId - ID of the schema
   * @param {string} userId - Optional ID of the user to assign
   *  the assessment to (if not provided, creates for all users)
   * @param {Object} options - Assessment creation options
   */
  createAssessmentFromSchema(schemaId, userId, options = {}) {
    const payload = { ...options };
    if (userId) {
      payload.userId = userId;
    }
    return adminApi.post(`/schemas/${schemaId}/assessments`, payload);
  },

  /**
   * Generate test data for development purposes (DEV ONLY)
   * @param {string} schemaId - ID of the schema
   */
  generateTestData(schemaId) {
    return adminApi.post(`/schemas/${schemaId}/generate-test-data`, {});
  },

  /**
   * Generate random assessment data for selected users based on schema chart type
   * @param {string} schemaId - ID of the schema
   * @param {string[]} userIds - Array of user IDs
   */
  generateRandomData(schemaId, userIds) {
    return adminApi.post(`/schemas/${schemaId}/generate-random-data`, { userIds });
  },

  // ===== REFLECTION GUIDELINE CONFIGURATION METHODS =====

  /**
   * Get reflection guideline configuration for a schema
   * @param {string} schemaId - ID of the schema
   */
  getReflexionGuidelineConfig(schemaId) {
    return adminApi.get(`/schemas/${schemaId}/reflexion-guideline-config`);
  },

  /**
   * Get all schemas with reflection guidelines enabled
   */
  getSchemasWithReflexionGuidelines() {
    return adminApi.get('/schemas/reflexion-guidelines/enabled');
  },

  /**
   * Get notification templates for a schema
   * @param {string} schemaId - ID of the schema
   */
  async getSchemaTemplates(schemaId) {
    // Use notification service API for schema templates
    return notificationTemplateService.getSchemaTemplates(schemaId);
  },

  /**
   * Update notification templates for a schema
   * @param {string} schemaId - ID of the schema
   * @param {Object} templates - Template configuration
   */
  async updateSchemaTemplates(schemaId, templates) {
    // Use notification service API for schema templates
    return notificationTemplateService.updateSchemaTemplates(schemaId, templates);
  },

};
